/**
 * Dinero en centavos enteros (D4).
 *
 * Regla del proyecto: el dinero NUNCA se representa en punto flotante ni en
 * unidades mayores. Todo importe es un entero de centavos.
 */

export type Cents = number;

/** Kilogramos con 3 decimales, como en el modelo (`numeric(12,3)`). */
export type Kg = number;

export class MoneyError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'MoneyError';
  }
}

/** Redondeo determinístico a entero: mitad hacia arriba en valor absoluto. */
export function roundHalfAwayFromZero(value: number): number {
  if (!Number.isFinite(value)) {
    throw new MoneyError(`Valor no finito: ${value}`);
  }
  return value < 0 ? -Math.round(-value) : Math.round(value);
}

/** Redondea kilogramos a 3 decimales, igual que `numeric(12,3)` en la base. */
export function roundKg(kg: number): Kg {
  if (!Number.isFinite(kg)) {
    throw new MoneyError(`Kilogramos no finitos: ${kg}`);
  }
  return roundHalfAwayFromZero(kg * 1000) / 1000;
}

export function assertCents(value: number, label = 'importe'): Cents {
  if (!Number.isInteger(value)) {
    throw new MoneyError(`El ${label} debe ser un entero de centavos, se recibió ${value}`);
  }
  if (!Number.isSafeInteger(value)) {
    throw new MoneyError(`El ${label} excede el rango seguro de enteros: ${value}`);
  }
  return value;
}

export function sumCents(values: readonly Cents[]): Cents {
  return values.reduce<Cents>((acc, v) => acc + v, 0);
}

/**
 * Convierte kilogramos por un precio unitario en centavos a un importe en centavos.
 *
 * Trabaja sobre milésimas de kg para eliminar el error de punto flotante y hace
 * la multiplicación en `BigInt`: `kgMilli × precio` supera `2^53` con valores
 * grandes pero posibles (p. ej. 10.000 km × $1.000.000 por km), y ahí `number`
 * empieza a perder centavos. PostgreSQL calcula en `numeric`, que es exacto;
 * para que el test de paridad pueda exigir coincidencia al centavo, la versión
 * TypeScript también tiene que serlo.
 */
export function kgToCents(kg: Kg, pricePerKgCents: Cents): Cents {
  assertCents(pricePerKgCents, 'precio por kg');
  const kgMilli = roundHalfAwayFromZero(kg * 1000);

  // round(n / 1000), mitad hacia arriba en valor absoluto, en aritmética exacta.
  const n = BigInt(kgMilli) * BigInt(pricePerKgCents);
  const negativo = n < 0n;
  const abs = negativo ? -n : n;
  const redondeado = (abs + 500n) / 1000n;
  return Number(negativo ? -redondeado : redondeado);
}
